import type { FluxErrorEntityErrorCodes } from '../types.js';

export const CardAssigneeErrorCodes = {
  CardAssigneeAlreadyExists: {
    code: 'card-assignee/already-exists',
    explanation: 'O usuário que você está tentando atribuir a este card já está atribuído a ele.',
    localizations: {},
    message: 'O usuário já está atribuído a este card.',
    supportingDocs: [
      {
        name: 'Gerenciamento de Responsáveis',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  CardAssigneeNotAssigned: {
    code: 'card-assignee/not-assigned',
    explanation: 'O usuário que você está tentando remover deste card não está atribuído a ele.',
    localizations: {},
    message: 'O usuário não está atribuído a este card.',
    supportingDocs: [
      {
        name: 'Gerenciamento de Responsáveis',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  CardAssigneeNotFound: {
    code: 'card-assignee/not-found',
    explanation: 'O usuário atribuído que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'O usuário atribuído não foi encontrado.',
  },
  CardAssigneeUserNotMember: {
    code: 'card-assignee/user-not-member',
    explanation:
      'O usuário que você está tentando atribuir a este card não é membro do fluxo (pipe). Por favor, adicione-o ao fluxo antes de continuar.',
    localizations: {},
    message:
      'O usuário atribuído não faz parte deste fluxo. Adicione-o ao fluxo antes de continuar.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-assignee'>;

export const CardAttachmentErrorCodes = {
  CardAttachmentNotFound: {
    code: 'card-attachment/not-found',
    explanation: 'O anexo de card que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'Não foi possível encontrar o anexo.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-attachment'>;

export const CardCommentAttachmentErrorCodes = {
  CardCommentAttachmentAlreadyExists: {
    code: 'card-comment-attachment/already-exists',
    explanation:
      'O anexo que você está tentando adicionar a este comentário já está associado a ele.',
    localizations: {},
    message: 'Este anexo já está associado a este comentário.',
  },
  CardCommentAttachmentLimitReached: {
    code: 'card-comment-attachment/limit-reached',
    explanation:
      'Este comentário já atingiu o número máximo de anexos permitidos. Para adicionar mais anexos, remova alguns dos anexos existentes.',
    localizations: {},
    message: 'O número máximo de anexos permitidos para este comentário foi atingido.',
  },
  CardCommentAttachmentNotFound: {
    code: 'card-comment-attachment/not-found',
    explanation: 'O anexo de comentário de card que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'Não foi possível encontrar o anexo do comentário.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-comment-attachment'>;

export const CardCommentErrorCodes = {
  CardCommentCreateFailed: {
    code: 'card-comment/create-failed',
    explanation:
      'Ocorreu um erro desconhecido ao tentar criar um comentário no card. Tente novamente mais tarde.',
    localizations: {},
    message: 'Não foi possível adicionar o comentário ao card.',
  },
  CardCommentNotFound: {
    code: 'card-comment/not-found',
    explanation: 'O comentário de card que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'O comentário especificado não foi encontrado.',
  },
  CardCommentNotOwner: {
    code: 'card-comment/not-owner',
    explanation:
      'Você não é o proprietário deste comentário de card e, portanto, não pode editá-lo ou excluí-lo.',
    localizations: {},
    message: 'Você não tem permissão para editar ou excluir este comentário.',
    supportingDocs: [
      {
        name: 'Aba de Comentários',
        path: '/wiki/docs/cards/tabs/comments',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-comment'>;

export const CardErrorCodes = {
  CardCreateFailed: {
    code: 'card/create-failed',
    explanation:
      'Ocorreu um erro ao tentar criar um novo card. Verifique se todos os campos obrigatórios estão preenchidos corretamente e tente novamente.',
    localizations: {},
    message:
      'Ocorreu um erro ao criar o processo. Verifique os dados fornecidos e tente novamente.',
    supportingDocs: [
      {
        name: 'Criação de Processos',
        path: '/wiki/docs/cards/intro',
      },
      {
        name: 'Criação de Solicitações',
        path: '/wiki/docs/cards/solicitations',
      },
    ],
  },
  CardIsAlreadyArchived: {
    code: 'card/is-already-archived',
    explanation: 'O card que você está tentando arquivar já está arquivado.',
    localizations: {},
    message: 'Este processo já está arquivado.',
  },
  CardIsNotArchived: {
    code: 'card/is-not-archived',
    explanation: 'O card que você está tentando desarquivar não está arquivado.',
    localizations: {},
    message: 'Este processo não está arquivado.',
  },
  CardIsSyncLocked: {
    code: 'card/is-sync-locked',
    explanation:
      'O card está atualmente bloqueado por uma automação síncrona. Aguarde a conclusão da automação ou tente novamente mais tarde.',
    localizations: {},
    message: 'O processo está bloqueado durante uma automação síncrona.',
  },
  CardLabelAlreadyLinked: {
    code: 'card/label-already-linked',
    explanation: 'A etiqueta que você está tentando associar a este card já está associada a ele.',
    localizations: {},
    message: 'Esta etiqueta já está associada ao processo.',
  },
  CardLabelNotLinked: {
    code: 'card/label-not-linked',
    explanation:
      'A etiqueta que você está tentando desassociar deste card não está associada a ele.',
    localizations: {},
    message: 'A etiqueta especificada não está associada a este processo.',
  },
  CardLocked: {
    code: 'card/locked',
    explanation:
      'O card está atualmente bloqueado e não pode ser editado. Isso pode ocorrer devido a automações, bloqueios manuais ou configurações de segurança. Cards bloqueados não permitem alterações ou movimentações até que sejam desbloqueados.',
    localizations: {},
    message:
      'O processo está bloqueado para edição. Isso pode ocorrer durante automações, manualmente ou por configurações de segurança.',
    supportingDocs: [
      {
        name: 'Revisão do Processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
  CardMoveSameStage: {
    code: 'card/move-same-stage',
    explanation:
      'Você não pode mover um card para a etapa em que ele já se encontra. Isso pode acontecer em automações caso o processo já esteja na etapa de destino.',
    localizations: {},
    message:
      'O processo já está nesta etapa do fluxo de trabalho. Selecione uma etapa diferente para movê-lo.',
  },
  CardNotFound: {
    code: 'card/not-found',
    explanation:
      'O card que você está tentando acessar não foi encontrado. Isso pode ocorrer se o card foi arquivado, removido ou se você não tem permissão para acessá-lo.',
    localizations: {},
    message:
      'O processo solicitado não foi encontrado. Ele pode ter sido arquivado, removido ou você pode não ter permissão para acessá-lo.',
  },
  CardNotSolicitation: {
    code: 'card/not-solicitation',
    explanation:
      'Este card não é uma solicitação. Somente cards de solicitação podem executar esta ação.',
    localizations: {},
    message: 'Este processo não é uma solicitação.',
    supportingDocs: [
      {
        name: 'Solicitações de Cards',
        path: '/wiki/docs/cards/solicitations',
      },
    ],
  },
  CardReadonly: {
    code: 'card/readonly',
    explanation:
      'O card está em modo somente leitura e não pode ser editado. Isso pode ocorrer devido a restrições de permissão ou configurações do fluxo.',
    localizations: {},
    message: 'O processo está em modo somente leitura.',
    supportingDocs: [
      {
        name: 'Revisão do Processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
      {
        name: 'Permissões do Card',
        path: '/wiki/docs/roles-and-permissions/permissions/permissions-fluxos#4-permissões-de-cards',
      },
    ],
  },
  CardReadonlyBySituation: {
    code: 'card/readonly-by-situation',
    explanation:
      'O card não pode ser editado devido à sua situação atual (por exemplo, uma revisão pendente). Isso pode ser devido a uma revisão pendente ou outra restrição do fluxo de trabalho.',
    localizations: {},
    message: 'O processo está em uma situação que não permite edição no momento.',
    supportingDocs: [
      {
        name: 'Revisão do Processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
  CardStageNotFound: {
    code: 'card/stage-not-found',
    explanation: 'A etapa atual do processo não foi encontrada no fluxo de trabalho.',
    localizations: {},
    message: 'A etapa atual do card não foi encontrada.',
  },
  CardUpdateFailed: {
    code: 'card/update-failed',
    explanation:
      'Ocorreu um erro desconhecido ao tentar atualizar o card. Verifique os dados fornecidos e tente novamente.',
    localizations: {},
    message: 'Não foi possível atualizar o processo.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'card'>;

export const CardReviewErrorCodes = {
  CardReviewRequestedChanges: {
    code: 'card-review/requested-changes',
    explanation:
      'Foram solicitadas alterações para a revisão deste card e elas precisam ser atendidas antes de prosseguir.',
    localizations: {},
    message: 'Alterações foram solicitadas para esta revisão do card.',
    supportingDocs: [
      {
        name: 'Revisões de Cards',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-review'>;

export const CardSolicitationErrorCodes = {
  CardSolicitationForbidden: {
    code: 'card-solicitation/forbidden',
    explanation: 'Você não tem permissão para enviar esta solicitação de card.',
    localizations: {},
    message: 'Você não pode enviar essa solicitação no momento.',
  },
  CardSolicitationNotFormResponsible: {
    code: 'card-solicitation/not-form-responsible',
    explanation: 'Você não é o responsável por este formulário e, portanto, não pode enviá-lo.',
    localizations: {},
    message: 'Você não é responsável por este formulário e não pode enviá-lo.',
    supportingDocs: [
      {
        name: 'Solicitações de Cards',
        path: '/wiki/docs/cards/solicitations',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-solicitation'>;

export const CardTaskAssigneeErrorCodes = {
  CardTaskAssigneeAlreadyExists: {
    code: 'card-task-assignee/already-exists',
    explanation:
      'O usuário que você está tentando atribuir a esta tarefa de card já está atribuído a ela.',
    localizations: {},
    message: 'O usuário já está atribuído a esta tarefa.',
  },
  CardTaskAssigneeNotAssigned: {
    code: 'card-task-assignee/not-assigned',
    explanation:
      'O usuário que você está tentando remover desta tarefa de card não está atribuído a ela.',
    localizations: {},
    message: 'O usuário não está atribuído a esta tarefa.',
  },
  CardTaskAssigneeNotFound: {
    code: 'card-task-assignee/not-found',
    explanation:
      'O responsável da tarefa de card que você está tentando acessar não foi encontrado. Ele pode ter sido removido ou você pode não ter permissão para acessá-lo.',
    localizations: {},
    message: 'O responsável da tarefa não foi encontrado.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-task-assignee'>;

export const CardTaskErrorCodes = {
  CardTaskBlockedByParent: {
    code: 'card-task/blocked-by-parent',
    explanation:
      'Esta tarefa de card não pode ser concluída porque sua tarefa pai ainda não foi concluída. Complete primeiro a tarefa da qual esta depende para poder prosseguir.',
    localizations: {},
    message: 'Esta tarefa não pode ser finalizada porque sua tarefa pai ainda não foi concluída.',
    supportingDocs: [
      {
        name: 'Tarefas Bloqueadas',
        path: '/wiki/docs/cards/tasks/blocked-tasks',
      },
    ],
  },
  CardTaskBlockedByStage: {
    code: 'card-task/blocked-by-stage',
    explanation:
      'Esta tarefa de card não pode ser concluída porque foi criada em uma etapa posterior à etapa atual do card. Mova o processo para a etapa correspondente ou aguarde a progressão natural do fluxo de trabalho para poder completar esta tarefa.',
    localizations: {},
    message:
      'Esta tarefa foi criada em uma etapa à frente da etapa atual do processo e não pode ser finalizada ainda.',
    supportingDocs: [
      {
        name: 'Tarefas Bloqueadas',
        path: '/wiki/docs/cards/tasks/blocked-tasks',
      },
    ],
  },
  CardTaskCardIdRequired: {
    code: 'card-task/card-id-required',
    explanation: 'O ID do card é obrigatório para realizar esta ação na tarefa de card.',
    localizations: {},
    message: 'O ID do card é obrigatório para realizar esta ação.',
  },
  CardTaskCardNotFound: {
    code: 'card-task/card-not-found',
    explanation:
      'O card associado a esta tarefa de card não foi encontrado. Verifique se o processo ainda existe, se não foi arquivado ou se você tem permissão para acessá-lo.',
    localizations: {},
    message: 'O processo associado a esta tarefa não foi encontrado.',
    supportingDocs: [
      {
        name: 'Criando uma Tarefa',
        path: '/wiki/docs/cards/tasks/creating-a-task',
      },
    ],
  },
  CardTaskCommentRequired: {
    code: 'card-task/comment-required',
    explanation:
      'A configuração desta tarefa exige que um comentário seja adicionado ao concluí-la.',
    localizations: {},
    message: 'Um comentário é obrigatório para concluir esta tarefa.',
    supportingDocs: [
      {
        name: 'Tarefas de Cards',
        path: '/wiki/docs/cards/tasks/creating-a-task#configuração-avançada-descrição-obrigatória-para-finalizar',
      },
    ],
  },
  CardTaskCreateFailed: {
    code: 'card-task/create-failed',
    explanation:
      'Ocorreu um erro ao tentar criar uma nova tarefa no card. Verifique se você tem permissão para criar tarefas, se o template da tarefa existe e se todos os campos obrigatórios estão preenchidos corretamente.',
    localizations: {},
    message: 'Não foi possível criar a tarefa no processo.',
    supportingDocs: [
      {
        name: 'Criando uma Tarefa',
        path: '/wiki/docs/cards/tasks/creating-a-task',
      },
    ],
  },
  CardTaskDeleteCreatedByStage: {
    code: 'card-task/delete-created-by-stage',
    explanation:
      'Esta tarefa de card não pode ser excluída porque foi criada a partir de um modelo de etapa.',
    localizations: {},
    message: 'Essa tarefa não pode ser excluída pois ela foi criada de um modelo da etapa.',
    supportingDocs: [
      {
        name: 'Tarefas de Etapas',
        path: '/wiki/docs/cards/tasks/stage-tasks',
      },
      {
        name: 'Modelos de Tarefas',
        path: '/wiki/docs/pipe/templates/tasks',
      },
    ],
  },
  CardTaskDueDateInThePast: {
    code: 'card-task/due-date-in-the-past',
    explanation: 'A data de vencimento da tarefa de card não pode ser uma data no passado.',
    localizations: {},
    message: 'A data de vencimento da tarefa não pode ser no passado.',
  },
  CardTaskInvalidDueDateFilter: {
    code: 'card-task/invalid-due-date-filter',
    explanation: 'O filtro de data de vencimento especificado para a tarefa de card é inválido.',
    localizations: {},
    message: 'O filtro de data de vencimento especificado é inválido.',
  },
  CardTaskMaxTasksReached: {
    code: 'card-task/max-tasks-reached',
    explanation:
      'Este card já atingiu o número máximo de tarefas permitidas. Remova algumas tarefas existentes para adicionar novas.',
    localizations: {},
    message: 'O número máximo de tarefas permitidas foi atingido para este processo.',
  },
  CardTaskNotFound: {
    code: 'card-task/not-found',
    explanation:
      'A tarefa de card que você está tentando acessar não foi encontrada. Ela pode ter sido removida, você pode não ter permissão para acessá-la, ou o ID pode estar incorreto.',
    localizations: {},
    message: 'A tarefa especificada não foi encontrada.',
  },
  CardTaskParentCardMismatch: {
    code: 'card-task/parent-card-mismatch',
    explanation: 'O card da tarefa pai não é o mesmo que o card da tarefa atual.',
    localizations: {},
    message: 'O card da tarefa pai não corresponde ao card da tarefa atual.',
  },
  CardTaskParentTaskSelf: {
    code: 'card-task/parent-task-self',
    explanation: 'Uma tarefa de card não pode ser sua própria tarefa pai.',
    localizations: {},
    message: 'Uma tarefa não pode depender de si mesma.',
  },
  CardTaskPipeIdRequired: {
    code: 'card-task/pipe-id-required',
    explanation: 'O ID do fluxo (pipe) é obrigatório para criar uma tarefa de card.',
    localizations: {},
    message: 'O ID do fluxo é obrigatório para criar uma tarefa.',
  },
  CardTaskRequiredFieldsNotAnswered: {
    code: 'card-task/required-fields-not-answered',
    explanation:
      'Um ou mais campos obrigatórios no formulário da tarefa de card não foram preenchidos.',
    localizations: {},
    message: 'O formulário não foi preenchido corretamente. Verifique os campos obrigatórios.',
    supportingDocs: [
      {
        name: 'Criando uma Tarefa',
        path: '/wiki/docs/cards/tasks/creating-a-task#formulário-personalizado',
      },
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
    ],
  },
  CardTaskUpdateCreatedByStage: {
    code: 'card-task/update-created-by-stage',
    explanation:
      'Esta tarefa de card não pode ser atualizada porque foi criada a partir de um modelo de etapa.',
    localizations: {},
    message: 'Essa tarefa não pode ser atualizada pois ela foi criada de um modelo da etapa.',
    supportingDocs: [
      {
        name: 'Tarefas de Etapas',
        path: '/wiki/docs/cards/tasks/stage-tasks',
      },
      {
        name: 'Modelos de Tarefas',
        path: '/wiki/docs/pipe/templates/tasks',
      },
    ],
  },
  CardTaskUpdateFailed: {
    code: 'card-task/update-failed',
    explanation:
      'Ocorreu um erro ao tentar atualizar as informações da tarefa de card. Verifique se você tem permissão para editar, se todos os campos obrigatórios estão preenchidos corretamente e se a tarefa não está bloqueada.',
    localizations: {},
    message: 'Não foi possível atualizar a tarefa.',
  },
  CardTaskUpdateWhileCompleted: {
    code: 'card-task/update-while-completed',
    explanation: 'Você não pode atualizar uma tarefa de card que já foi concluída.',
    localizations: {},
    message: 'Não é possível atualizar a tarefa porque ela já foi concluída.',
    supportingDocs: [
      {
        name: 'Alterando o Status de uma Tarefa',
        path: '/wiki/docs/cards/tasks/updating-task-status',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-task'>;

export const CardTaskLabelErrorCodes = {
  CardTaskLabelAlreadyLinked: {
    code: 'card-task-label/already-linked',
    explanation:
      'A etiqueta que você está tentando associar a esta tarefa de card já está associada a ela.',
    localizations: {},
    message: 'Esta etiqueta já está associada à tarefa.',
  },
  CardTaskLabelNotLinked: {
    code: 'card-task-label/not-linked',
    explanation:
      'A etiqueta que você está tentando desassociar desta tarefa de card não está associada a ela.',
    localizations: {},
    message: 'A etiqueta não está associada à tarefa.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-task-label'>;

export const CardDeadlineErrorCodes = {
  CardDeadlinePipeAlreadySet: {
    code: 'card-deadline/pipe-already-set',
    explanation:
      'A data de vencimento para este card já foi definida no fluxo, não é possível alterar uma vez que foi definida.',
    localizations: {},
    message: 'A data de vencimento já foi definida para este card no fluxo.',
  },
  CardDeadlineStageAlreadySet: {
    code: 'card-deadline/stage-already-set',
    explanation:
      'A data de vencimento para este card já foi definida na etapa, não é possível alterar uma vez que foi definida.',
    localizations: {},
    message: 'A data de vencimento já foi definida para este card na etapa.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'card-deadline'>;
